/*
 * Click-to-load Help Scout Beacon. Nothing from Help Scout loads on page
 * view: the script is injected the first time the visitor asks for chat (the
 * "Chat" pill or the navbar help button), so the chat is a service they
 * explicitly requested and needs no cookie consent. Once the Beacon is ready
 * its own button takes over from the pill.
 */

import Bugsnag from '@/bugsnag';
import config from '@/config';

/** Above cart (2000) and HelpScout’s default (1050) so the iframe isn’t covered. */
export const HELP_SCOUT_Z_INDEX = 2147483000;

/** The storefront's Help Scout Beacon (`NEXT_PUBLIC_HELPSCOUT_BEACON_ID`). Without one, chat opens the support site. */
export const BEACON_ID = config.helpScoutBeaconId;
export const BEACON_SCRIPT = 'https://beacon-v2.helpscout.net';
export const LOAD_TIMEOUT_MS = 8000;
export const IOS_LOAD_TIMEOUT_MS = 20000;
/** Launcher offset from the viewport edges, shared by the pill and the Beacon button. */
export const LAUNCHER_OFFSET_PX = 16;
/** On the dapp the launcher sits above the sticky "Powered by" bar. */
export const LAUNCHER_RAISED_OFFSET_PX = 56;
export const MODAL_POLL_INTERVAL_MS = 100;
export const MODAL_POLL_DURATION_MS = 2000;

export const CLASSIFICATION_BLOCKED = 'blocked_or_network';
export const CLASSIFICATION_READY_TIMEOUT = 'ready_timeout_after_load';
export const CLASSIFICATION_OPEN_NO_MODAL = 'open_no_modal';

let beaconLoadPromise = null;
let beaconReady = false;
let beaconInitRequested = false;
let openQueued = false;
let initQueued = false;
let scriptInjected = false;
let scriptLoaded = false;
let scriptError = null;
let lastFailureStage = null;
let lastClassification = null;
let userFailureReported = false;
let userReinitAttempted = false;
let readyListenerAttached = false;
let beaconLoadFailed = false;
/** Resolvers waiting on the current load attempt (cleared on settle). */
let pendingLoadResolvers = null;
/** Opens the support site when chat cannot load; set by <HelpScoutChat />. */
let fallbackHandler = null;
let fallbackTimerId = null;
let openAttempt = null;
let openListenerAttached = false;
let lastStatus = 'idle';
const statusListeners = new Set();

/**
 * Where the Beacon is in its life:
 * - `idle`: nothing from Help Scout has been requested.
 * - `loading`: the visitor asked for chat; the script is on its way.
 * - `ready`: the Beacon is initialised (its own button is on the page).
 * - `failed`: the script was blocked or timed out; chat falls back to the
 *   support site.
 */
export function getBeaconStatus() {
  if (beaconReady) return 'ready';
  if (beaconLoadFailed || scriptError) return 'failed';
  if (beaconInitRequested) return 'loading';
  return 'idle';
}

function emitStatus() {
  const status = getBeaconStatus();
  if (status === lastStatus) return;
  lastStatus = status;
  for (const listener of [...statusListeners]) listener();
}

/** `useSyncExternalStore` subscriber for `getBeaconStatus`. */
export function subscribeBeacon(listener) {
  statusListeners.add(listener);
  return () => {
    statusListeners.delete(listener);
  };
}

/** The embedded dapp (`/dapp`, any locale) has a sticky bar at the bottom. */
export function isDappPath(pathname) {
  const path =
    typeof pathname === 'string'
      ? pathname
      : typeof window !== 'undefined'
        ? window.location?.pathname || ''
        : '';
  return /(^|\/)dapp(\/|$)/.test(path);
}

function isBeaconStub() {
  return (
    typeof window !== 'undefined' &&
    typeof window.Beacon === 'function' &&
    Array.isArray(window.Beacon.readyQueue)
  );
}

export function isIOS() {
  if (typeof navigator === 'undefined') return false;

  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

/**
 * Kept for existing callers. Every platform now uses Help Scout's own button
 * once the Beacon has loaded, so this no longer changes the init payload.
 */
export function shouldUseNativeIosFab() {
  return isIOS();
}

export function getLoadTimeoutMs() {
  return isIOS() ? IOS_LOAD_TIMEOUT_MS : LOAD_TIMEOUT_MS;
}

export function hasBeaconInitStarted() {
  return beaconInitRequested || beaconReady;
}

export function isBeaconReady() {
  return beaconReady;
}

export function hasBeaconLoadFailed() {
  return beaconLoadFailed || Boolean(scriptError);
}

function markBeaconLoadFailed() {
  beaconLoadFailed = true;
  emitStatus();
}

export function isWebViewUserAgent() {
  return typeof navigator !== 'undefined' && /WebView|wv\)/i.test(navigator.userAgent);
}

/** Wallet WebViews / in-app browsers that often block Beacon. */
export function isHostileBeaconEnvironment() {
  if (typeof window === 'undefined') return false;

  if (isWebViewUserAgent()) return true;
  if (window.bidaliProvider || window.valora) return true;

  try {
    if (new URLSearchParams(window.location.search).has('xAppToken')) return true;
  } catch {
    // ignore
  }

  return false;
}

function getConnectionInfo() {
  if (typeof navigator === 'undefined') return null;

  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!connection) return null;

  return {
    effectiveType: connection.effectiveType || null,
    saveData: Boolean(connection.saveData),
  };
}

export function classifyBeaconFailure(stage) {
  if (stage === 'open_no_modal') return CLASSIFICATION_OPEN_NO_MODAL;
  if (stage === 'script_error' || scriptError || !scriptLoaded) {
    return CLASSIFICATION_BLOCKED;
  }
  return CLASSIFICATION_READY_TIMEOUT;
}

export function getBeaconDiagnostics() {
  return {
    stage: lastFailureStage,
    classification: lastClassification,
    beaconReady,
    beaconInitRequested,
    scriptInjected,
    scriptLoaded,
    scriptError: scriptError?.message || null,
    modalVisible: isBeaconModalVisible(),
    isIOS: isIOS(),
    isWebView: isWebViewUserAgent(),
    isHostile: isHostileBeaconEnvironment(),
    connection: getConnectionInfo(),
    path: typeof window !== 'undefined' ? window.location?.pathname || '' : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  };
}

export function ensureBeaconStub() {
  if (typeof window === 'undefined' || typeof window.Beacon === 'function') return;

  const beacon = (method, options, data) => {
    window.Beacon.readyQueue.push({ method, options, data });
  };
  beacon.readyQueue = [];
  window.Beacon = beacon;
}

/**
 * Help Scout's own button, where the "Chat" pill was, so the hand-over does
 * not move the launcher.
 */
function getBeaconInitPayload() {
  return {
    beaconId: BEACON_ID,
    display: {
      zIndex: HELP_SCOUT_Z_INDEX,
      style: 'icon',
      position: 'right',
      horizontalOffset: LAUNCHER_OFFSET_PX,
      verticalOffset: LAUNCHER_OFFSET_PX,
      horizontalMobileOffset: LAUNCHER_OFFSET_PX,
      verticalMobileOffset: isDappPath() ? LAUNCHER_RAISED_OFFSET_PX : LAUNCHER_OFFSET_PX,
    },
  };
}

function leaveBeaconBreadcrumb(message, metadata = {}) {
  try {
    Bugsnag.leaveBreadcrumb(message, { ...getBeaconDiagnostics(), ...metadata });
  } catch {
    // ignore
  }
}

/**
 * Notify Bugsnag only for user-intent failures, once per page load.
 * Preload / background timeouts breadcrumb only.
 */
function notifyBeaconFailure(stage, { intent = 'user', error } = {}) {
  lastFailureStage = stage;
  const classification = classifyBeaconFailure(stage);
  lastClassification = classification;

  leaveBeaconBreadcrumb(`HelpScout Beacon failure: ${stage}`, { classification, intent });

  if (intent !== 'user') return;
  if (userFailureReported) return;
  userFailureReported = true;

  const severity = classification === CLASSIFICATION_BLOCKED ? 'warning' : 'error';
  const message =
    error?.message ||
    (classification === CLASSIFICATION_BLOCKED
      ? 'HelpScout Beacon blocked or unreachable'
      : classification === CLASSIFICATION_OPEN_NO_MODAL
        ? 'HelpScout Beacon ready but modal not visible'
        : 'Help Scout Beacon timed out after script load');

  Bugsnag.notify(error || new Error(message), (event) => {
    event.severity = severity;
    event.groupingHash = `helpscout:${classification}`;
    event.addMetadata('helpscout', {
      ...getBeaconDiagnostics(),
      classification,
      intent,
    });
  });
}

function isElementVisible(element) {
  if (!element) return false;

  const rect = element.getBoundingClientRect();
  const styles = window.getComputedStyle(element);
  return (
    rect.width > 0 &&
    rect.height > 0 &&
    styles.display !== 'none' &&
    styles.visibility !== 'hidden' &&
    styles.opacity !== '0'
  );
}

/** Insert script immediately — never wait for window.load (WebView miss). */
export function insertBeaconScript() {
  if (typeof document === 'undefined') return;
  if (document.querySelector('script[src*="beacon-v2.helpscout.net"]')) {
    scriptInjected = true;
    return;
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = BEACON_SCRIPT;
  scriptInjected = true;

  script.onload = () => {
    scriptLoaded = true;
    leaveBeaconBreadcrumb('HelpScout script loaded');
  };

  script.onerror = () => {
    scriptError = new Error('HelpScout script failed to load');
    markBeaconLoadFailed();
    leaveBeaconBreadcrumb('HelpScout script error', {
      classification: CLASSIFICATION_BLOCKED,
      intent: 'preload',
    });

    if (pendingLoadResolvers) {
      const { reject } = pendingLoadResolvers;
      pendingLoadResolvers = null;
      beaconLoadPromise = null;
      reject(scriptError);
    }
  };

  const first = document.getElementsByTagName('script')[0];
  if (first?.parentNode) {
    first.parentNode.insertBefore(script, first);
  } else {
    (document.head || document.body || document.documentElement)?.appendChild(script);
  }
}

export function isBeaconModalVisible() {
  if (typeof document === 'undefined') return false;

  const modal = document.querySelector('.BeaconContainer');
  if (isElementVisible(modal)) return true;

  const container = document.getElementById('beacon-container');
  if (isElementVisible(container)) return true;

  const chatIframe = document.querySelector(
    '#beacon-container iframe[title*="Help Scout Beacon"], iframe[title*="Help Scout Beacon - Live Chat"]'
  );
  return isElementVisible(chatIframe);
}

/**
 * Persistent ready listener so a late ready (after idle timeout) still marks
 * Beacon usable for a later click.
 */
function attachReadyListener() {
  if (readyListenerAttached || typeof window.Beacon !== 'function') return;

  readyListenerAttached = true;
  window.Beacon('once', 'ready', () => {
    beaconReady = true;
    emitStatus();
    leaveBeaconBreadcrumb('HelpScout ready');

    if (pendingLoadResolvers) {
      const { resolve } = pendingLoadResolvers;
      pendingLoadResolvers = null;
      resolve();
    }
  });
}

function queueOpenOnReady() {
  if (openQueued || typeof window.Beacon !== 'function') return;

  openQueued = true;
  window.Beacon('once', 'ready', () => {
    window.Beacon('open');
    leaveBeaconBreadcrumb('HelpScout open queued on ready');
  });
}

function queueBeaconInit({ force = false } = {}) {
  if (typeof window.Beacon !== 'function') return;

  if (initQueued && !force) return;

  initQueued = true;
  window.Beacon('init', getBeaconInitPayload());
  leaveBeaconBreadcrumb(force ? 'HelpScout init re-queued' : 'HelpScout init queued');
}

function ensureLoadPromise() {
  if (beaconReady) return Promise.resolve();
  if (scriptError) return Promise.reject(scriptError);
  if (beaconLoadPromise) return beaconLoadPromise;

  beaconLoadPromise = new Promise((resolve, reject) => {
    const timeoutMs = getLoadTimeoutMs();
    const timeoutId = window.setTimeout(() => {
      markBeaconLoadFailed();
      leaveBeaconBreadcrumb('HelpScout ready timeout', { intent: 'preload' });
      if (pendingLoadResolvers) {
        pendingLoadResolvers = null;
      }
      reject(new Error('Help Scout Beacon timed out'));
    }, timeoutMs);

    pendingLoadResolvers = {
      resolve: () => {
        window.clearTimeout(timeoutId);
        resolve();
      },
      reject: (error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      },
    };

    attachReadyListener();

    // If ready already fired between begin and ensure, settle immediately.
    if (beaconReady) {
      pendingLoadResolvers.resolve();
      pendingLoadResolvers = null;
    }
  }).catch((error) => {
    if (!beaconReady) {
      beaconLoadPromise = null;
    }
    throw error;
  });

  return beaconLoadPromise;
}

/**
 * Synchronously start Beacon load (stub, script, init). Safe to call from touchstart/click.
 */
export function beginBeaconLoad({ forceReinit = false } = {}) {
  if (typeof window === 'undefined' || !BEACON_ID) return;

  beaconInitRequested = true;
  ensureBeaconStub();
  emitStatus();

  if (beaconReady) return;

  if (typeof window.Beacon === 'function' && !isBeaconStub()) {
    try {
      const info = window.Beacon('info');
      if (info?.beaconId) {
        beaconReady = true;
        emitStatus();
        return;
      }
    } catch {
      // Already initialized but not ready — wait for existing promise.
    }
  }

  insertBeaconScript();
  attachReadyListener();
  // Fire-and-forget: callers that care await loadBeacon / requestBeaconOpen.
  void ensureLoadPromise().catch(() => {});
  queueBeaconInit({ force: forceReinit });
}

/**
 * Synchronously request open on tap — queues open before any await (iOS gesture safety).
 */
export function requestBeaconOpen() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Beacon requires a browser'));
  }
  if (!BEACON_ID) {
    return Promise.reject(new Error('No Help Scout Beacon ID configured'));
  }

  if (scriptError) {
    return Promise.reject(scriptError);
  }

  if (beaconReady && typeof window.Beacon === 'function') {
    window.Beacon('open');
    leaveBeaconBreadcrumb('HelpScout open immediate');
    return Promise.resolve();
  }

  // Script loaded but never became ready (e.g. idle preload timed out): re-init once.
  const shouldReinit = scriptLoaded && !beaconReady && initQueued && !userReinitAttempted;
  if (shouldReinit) {
    userReinitAttempted = true;
  }

  beginBeaconLoad({ forceReinit: shouldReinit });
  queueOpenOnReady();
  return ensureLoadPromise();
}

export function waitForBeaconModal(maxMs = MODAL_POLL_DURATION_MS) {
  if (typeof window === 'undefined') {
    return Promise.resolve(false);
  }

  if (isBeaconModalVisible()) {
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      if (isBeaconModalVisible()) {
        resolve(true);
        return;
      }
      if (Date.now() - started >= maxMs) {
        resolve(false);
        return;
      }
      window.setTimeout(tick, MODAL_POLL_INTERVAL_MS);
    };
    tick();
  });
}

/**
 * Load + init Help Scout once. Resolves on the ready event.
 */
export function loadBeacon() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Beacon requires a browser'));
  }
  if (!BEACON_ID) {
    return Promise.reject(new Error('No Help Scout Beacon ID configured'));
  }

  beginBeaconLoad();

  if (beaconReady) {
    return Promise.resolve();
  }

  return ensureLoadPromise();
}

/**
 * Kept for existing callers (the dapp support button). It no longer loads
 * anything by itself: Help Scout only loads once the visitor asks for chat.
 * After that it resolves when the Beacon is ready. Never throws.
 */
export function preloadBeacon() {
  if (!hasBeaconInitStarted()) return Promise.resolve();
  return loadBeacon().catch(() => {});
}

export function openBeacon() {
  return requestBeaconOpen();
}

export function closeBeacon() {
  if (beaconReady && typeof window.Beacon === 'function') {
    window.Beacon('close');
  }
}

export function toggleBeacon() {
  if (beaconReady && isBeaconModalVisible()) {
    closeBeacon();
    return Promise.resolve();
  }

  return requestBeaconOpen();
}

export function retryBeaconOpen() {
  if (typeof window === 'undefined' || typeof window.Beacon !== 'function') {
    return;
  }

  window.Beacon('open');
  leaveBeaconBreadcrumb('HelpScout open retry');
}

/** Single Bugsnag notify path for user-facing chat fallback. */
export function reportBeaconFallback(stage) {
  markBeaconLoadFailed();
  notifyBeaconFailure(stage, {
    intent: 'user',
    error: new Error(`HelpScout Beacon fallback: ${stage}`),
  });
}

function clearFallbackTimer() {
  if (fallbackTimerId !== null) {
    window.clearTimeout(fallbackTimerId);
    fallbackTimerId = null;
  }
}

/**
 * How the support site is opened when chat cannot load. <HelpScoutChat />
 * registers the dapp-aware opener; the default is a new tab. Returns a
 * function that removes the handler.
 */
export function setSupportFallbackHandler(handler) {
  fallbackHandler = handler;
  return () => {
    if (fallbackHandler === handler) fallbackHandler = null;
  };
}

function openSupportSite() {
  if (typeof fallbackHandler === 'function') {
    fallbackHandler(config.supportUrl);
  } else {
    window.open(config.supportUrl, '_blank', 'noopener,noreferrer');
  }
}

function runSupportFallback(stage) {
  clearFallbackTimer();
  reportBeaconFallback(stage);
  openSupportSite();
}

/**
 * The visitor asked for chat. Loads and initialises the Beacon on the first
 * call (synchronously, so it stays inside the tap gesture), then opens it.
 * If Help Scout is blocked, times out, or its window never shows, the support
 * site opens instead; once chat has failed, later calls go straight there.
 *
 * Resolves with 'opened', 'fallback' or 'pending' (still waiting on the
 * fallback timer). Never rejects.
 */
export function openSupportChat() {
  if (typeof window === 'undefined') return Promise.resolve('fallback');
  // No Beacon configured: there is no chat to load, and nothing failed.
  if (!BEACON_ID) {
    openSupportSite();
    return Promise.resolve('fallback');
  }
  if (openAttempt) return openAttempt;

  if (getBeaconStatus() === 'failed') {
    runSupportFallback(scriptError ? 'script_error' : 'ready_timeout');
    return Promise.resolve('fallback');
  }

  let fellBack = false;
  const fallback = (stage) => {
    if (fellBack) return;
    fellBack = true;
    runSupportFallback(stage);
  };

  clearFallbackTimer();
  fallbackTimerId = window.setTimeout(() => {
    fallbackTimerId = null;
    if (isBeaconModalVisible()) return;
    if (scriptError) {
      fallback('script_error');
      return;
    }
    fallback(beaconReady ? 'open_no_modal' : 'ready_timeout');
  }, getLoadTimeoutMs());

  const request = requestBeaconOpen();

  // The stub exists now: stop the fallback as soon as the chat window opens.
  if (!openListenerAttached && typeof window.Beacon === 'function') {
    openListenerAttached = true;
    window.Beacon('on', 'open', clearFallbackTimer);
  }

  openAttempt = request
    .then(async () => {
      if (await waitForBeaconModal()) {
        clearFallbackTimer();
        return 'opened';
      }

      retryBeaconOpen();
      if (await waitForBeaconModal()) {
        clearFallbackTimer();
        return 'opened';
      }

      return fellBack ? 'fallback' : 'pending';
    })
    .catch((error) => {
      fallback(
        error?.message === 'HelpScout script failed to load' ? 'script_error' : 'ready_timeout'
      );
      return 'fallback';
    })
    .finally(() => {
      openAttempt = null;
    });

  return openAttempt;
}

/** Navbar help button: closes the chat window when it is open, opens it otherwise. */
export function toggleSupportChat() {
  if (beaconReady && isBeaconModalVisible()) {
    closeBeacon();
    return Promise.resolve('closed');
  }

  return openSupportChat();
}

/**
 * Kept for existing callers (the dapp support button probes the Beacon when
 * this is true). There is no idle preload any more, so there is nothing to
 * defer and nothing to probe: Help Scout only loads when the visitor asks.
 */
export function shouldDeferIdlePreload() {
  return false;
}

/** @internal testing only */
export function resetBeaconForTests() {
  beaconLoadPromise = null;
  beaconReady = false;
  beaconInitRequested = false;
  openQueued = false;
  initQueued = false;
  scriptInjected = false;
  scriptLoaded = false;
  scriptError = null;
  lastFailureStage = null;
  lastClassification = null;
  userFailureReported = false;
  userReinitAttempted = false;
  readyListenerAttached = false;
  beaconLoadFailed = false;
  pendingLoadResolvers = null;
  fallbackHandler = null;
  openAttempt = null;
  openListenerAttached = false;
  if (typeof window !== 'undefined') {
    clearFallbackTimer();
    delete window.Beacon;
  }
  fallbackTimerId = null;
  emitStatus();
}

/** @internal testing only — simulate script load/error without DOM. */
export function setScriptStateForTests({ loaded = false, error = null } = {}) {
  scriptInjected = true;
  scriptLoaded = loaded;
  scriptError = error;
  emitStatus();
}
